<template>
    <div>
        <div class="mb-3 text-right">
            <hts-form-button
                v-if="group.isAdmin || permissions.includes('supply_add')"
                type="submit"
                icon-name="hi-plus-circle-icon"
                icon-position="right"
                @click="onCreateSupply">
                {{ $t('labels.add') }}
            </hts-form-button>
        </div>
        <hts-data-table
            ref="tableSupply"
            :headers="supplyHeaders"
            :server="apiListSupply"
            :get-context-menu-items="getContextMenuItems" />
        <supply-editor
            v-if="showSupplyEditor"
            :show="showSupplyEditor"
            :action="supplyEditor"
            :supply-data="supplySelected"
            @close="showSupplyEditor = false"
            @on-submit="onSubmit" />
        <supply-history
            :show="showSlideOverHistorySupply"
            :supply-id="supplyId"
            @close="showSlideOverHistorySupply = false" />
        <supply-list-plugins
            v-if="showSupplyPlugins"
            :id="supplySelected.ID"
            :show="showSupplyPlugins"
            @close="showSupplyPlugins = false" />
        <supply-list-insurance
            v-if="showSupplyInsurance"
            :id="supplySelected.ID"
            :show="showSupplyInsurance"
            @close="showSupplyInsurance = false" />
        <supply-list-document
            v-if="showSupplyDocument"
            :id="supplySelected.ID"
            :show="showSupplyDocument"
            @close="showSupplyDocument = false" />
        <hts-modal
            :show="showModalDelete"
            :title="$t('labels.deleteSupply')"
            type="confirm"
            :content="$t('labels.confirmDeleteSupply')"
            @hide="onCloseModal"
            @confirm="() => onDelete(idsDelete)" />
    </div>
</template>

<script setup lang="ts">
import { ESupplyStatus, ESupplySubType, ESupplyType, ISupply } from './models/supply'
import { onHanlderStatus, onHanlderSubType, onHanlderType } from './service/helper'
import { route as pageTabs } from './service/router'

import SupplyEditor from './ignore/supply-editor.vue'
import SupplyHistory from './ignore/supply-history.vue'
import SupplyListDocument from './ignore/supply-list-document.vue'
import SupplyListInsurance from './ignore/supply-list-insurance.vue'
import SupplyListPlugins from './ignore/supply-list-plugins.vue'

const { $formatters } = useNuxtApp()
const store = $store()
const group = store.system.getGroup
const route = useRoute()
const permissions = ref<Array<String>>([])
const apiListSupply = ref({})
onBeforeMount(() => {
  route.meta.pageTabs = pageTabs
  apiListSupply.value = $url.supply.supply['supply-get-list']
  permissions.value = group.permissions
})
const supplyDefault: ISupply = {
  ID: '',
  name: '',
  price: 0,
  type: ESupplyType.FUEL,
  subType: ESupplySubType.MOOC,
  vendor: '',
  weightKG: 0,
  status: ESupplyStatus.DEFAULT,
  description: '',
  depreciationMonth: 0,
  priceLiquidation: 0,
  usedTotal: 0
}

const supplySelected: ISupply = reactive({ ...supplyDefault })

const supplyEditor = ref('create')

const supplyHeaders = [
  {
    field: 'name',
    headerName: $t('labels.supplyList.name'),
    sortable: true
  },
  {
    field: 'type',
    headerName: $t('labels.supplyList.type'),
    valueGetter: (params: any) => {
      return onHanlderType(params.data.type)
    },
    sortable: true
  },
  {
    field: 'subType',
    headerName: $t('labels.supplyList.subType'),
    valueGetter: (params: any) => {
      return onHanlderSubType(params.data.subType)
    },
    sortable: true
  },
  {
    field: 'vendor',
    headerName: $t('labels.supplyList.vendor'),
    sortable: true
  },
  {
    field: 'price',
    headerName: $t('labels.supplyList.price'),
    valueGetter: (params: any) => {
      return params.data.price.$numberDecimal
    },
    sortable: true
  },
  {
    field: 'weightKG',
    headerName: $t('labels.supplyList.weight'),
    sortable: true
  },
  {
    field: 'description',
    headerName: $t('labels.supplyList.description'),
    sortable: true
  },
  {
    field: 'status',
    headerName: $t('labels.supplyList.status'),
    valueGetter: (params: any) => {
      return onHanlderStatus(params.data.status)
    },
    sortable: true
  },
  {
    field: 'depreciationMonth',
    headerName: $t('labels.supplyList.depreciationMonth'),
    valueGetter: (params: any) => {
      if (params.data.depreciationMonth) {
        return params.data.depreciationMonth.$numberDecimal
      }
      return 'N.A'
    },
    sortable: true
  },
  {
    field: 'priceLiquidation',
    headerName: $t('labels.supplyList.priceLiquidation'),
    valueGetter: (params: any) => {
      if (params.data.priceLiquidation) {
        return params.data.priceLiquidation.$numberDecimal
      }
      return 'N.A'
    },
    sortable: true
  },
  {
    field: 'usedTotal',
    headerName: $t('labels.supplyList.usedTotal'),
    valueGetter: (params: any) => {
      if (params.data.usedTotal) {
        return params.data.usedTotal.$numberDecimal
      }
      return 'N.A'
    },
    sortable: true
  },
  {
    field: 'createdAt',
    headerName: $t('labels.createdAt'),
    valueGetter: (params: any) => {
      return $formatters.datetime(params.data.createdAt)
    },
    sortable: true
  },
  {
    field: 'updatedAt',
    headerName: $t('labels.updatedAt'),
    valueGetter: (params: any) => {
      return $formatters.datetime(params.data.updatedAt)
    },
    sortable: true
  }
]

const onCreateSupply = () => {
  supplyEditor.value = 'create'
  Object.assign(supplySelected, { ...supplyDefault })
  showSupplyEditor.value = true
}

const supplyId = ref()
const tableSupply = ref()
const getContextMenuItems = (params: any) => {
  if (params.node && params.node.data) {
    const menuItem = [
      {
        name: $t('labels.supplyHistory.viewHistory'),
        action: () => {
          supplyId.value = params.node?.data?._id
          showSlideOverHistorySupply.value = true
        }
      },
      group.isAdmin || permissions.value.includes('supply_edit')
        ? {
          name: $t('labels.supplyList.edit'),
          action: () => {
            supplySelected.ID = params.node.data.id
            supplySelected.name = params.node.data.name || ''
            supplySelected.type = params.node.data.type
            supplySelected.subType = params.node.data.subType
            supplySelected.price = params.node.data.price.$numberDecimal
            supplySelected.vendor = params.node.data.vendor || ''
            supplySelected.status = params.node.data.status
            supplySelected.weightKG = params.node.data.weightKG
            supplySelected.description = params.node.data.description || ''
            supplySelected.depreciationMonth = params.node.data.depreciationMonth ? params.node.data.depreciationMonth.$numberDecimal : ''
            supplySelected.priceLiquidation = params.node.data.priceLiquidation ? params.node.data.priceLiquidation.$numberDecimal : ''
            supplySelected.usedTotal = params.node.data.usedTotal ? params.node.data.usedTotal.$numberDecimal : ''
            supplyEditor.value = 'edit'
            showSupplyEditor.value = true
          }
        }
        : null,
      group.isAdmin || permissions.value.includes('supply_plugin_management')
        ? {
          name: $t('labels.supplyPlugin.view'),
          action: () => {
            supplySelected.ID = params.node.data.id
            showSupplyPlugins.value = true
          }
        }
        : null,
      group.isAdmin || permissions.value.includes('supply_insurance_management')
        ? {
          name: $t('labels.supplyInsurance.view'),
          action: () => {
            supplySelected.ID = params.node.data.id
            showSupplyInsurance.value = true
          }
        }
        : null,
      group.isAdmin || permissions.value.includes('supply_document_management')
        ? {
          name: $t('labels.supplyDocument.viewDocument'),
          action: () => {
            supplySelected.ID = params.node.data.id
            showSupplyDocument.value = true
          }
        }
        : null,
      group.isAdmin || permissions.value.includes('supply_delete')
        ? {
          name: $t('labels.supplyList.delete'),
          action: () => {
            const ids = [params.node.data.id]
            // onDelete(ids)
            onConfirmDelete(ids)
          }
        }
        : null
    ]
    return menuItem
  }
  return []
}
const showSlideOverHistorySupply = ref(false)
const showSupplyEditor = ref(false)
const showSupplyPlugins = ref(false)
const showSupplyInsurance = ref(false)
const showSupplyDocument = ref(false)
const showModalDelete = ref(false)
const idsDelete = ref<string[]>([])

const onConfirmDelete = (ids: string[]) => {
  showModalDelete.value = true
  idsDelete.value = ids
}

const onCloseModal = () => {
  showModalDelete.value = false
}

const onSubmit = $lodash.debounce(async (data: any, action: string) => {
  const url = action === 'create' ? $url.supply.supply.supplyCreate : $url.supply.supply.supplyUpdate
  const result = await $api(url, { body: new URLSearchParams(data) })
  const success = $lodash.get(result, 'data.value.success')
  const error = $lodash.get(result, 'data.value.data')
  if (success) {
    if (action === 'create') {
      $toast().success($t('labels.createSussess'))
    }
    if (action === 'edit') {
      $toast().success($t('labels.updateSuccess'))
    }
    tableSupply.value.refresh()
    showSupplyEditor.value = false
  } else {
    process.client && $toast().error(error.errors[0].msg)
  }
}, 500)

const onDelete = $lodash.debounce(async (ids: String[]) => {
  const url = $url.supply.supply.supplyDelete
  const result = await $api(url, { body: { ids } })
  const success = $lodash.get(result, 'data.value.success')
  const error = $lodash.get(result, 'data.value.data')
  if (success) {
    $toast().success($t('labels.deleteSuccess'))
    tableSupply.value.refresh()
    onCloseModal()
  } else {
    $toast().error(error)
  }
}, 500)
</script>
